/* eslint-disable max-lines */
import * as React from 'react';
import { clsx } from 'clsx';
import { useUiKitToastApiContext } from '../../context';
import { IconActionClose16, IconActionClose24, IconFunctionalWarningCircle24, IconFunctionalSuccessCircle24 } from '@cian/ui-kit-design-tokens/icons';
import { ArticleParagraph1, UIText2 } from '../../../typography';
import { Spinner } from '../../../loader/components/Spinner';
import { useGetIsMobileDevice } from './internal/hooks';
import { LeftAdornment } from './internal/components/LeftAdornment';
import { RightAdornment } from './internal/components/RightAdornment';
import { ProgressAdornment } from './internal/components/ProgressAdornment';
import { ProgressBar } from './internal/components/ProgressBar';
import * as styles from './Toast.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ToastComponent = props => {
  const ref = React.useRef(null);
  const toastsApiContext = useUiKitToastApiContext();
  const [currentHideTimerId, setCurrentHideTimerId] = React.useState(props.timerId);
  const isMobileDevice = useGetIsMobileDevice();
  const {
    type,
    size,
    placement,
    text,
    hidden,
    hideDelay,
    isHidable,
    content: contentRenderFunctionOrJSX,
    leftAdornment: leftAdornmentRenderFunctionOrJSX,
    rightAdornment: rightAdornmentRenderFunctionOrJSX,
    id,
    onClose,
    hasCloseIcon,
    confirmAction,
    dismissAction,
    confirmText = 'Посмотреть',
    dismissText = 'Пропустить',
    progressConfig,
    dataTestId
  } = props;
  const toastApi = toastsApiContext.apiByPlacement[placement];
  React.useEffect(() => {
    const classToAdd = hidden ? styles['hidden'] : styles['visible'];
    const classToRemove = hidden ? styles['visible'] : styles['hidden'];

    /**
     * Не убирать таймаут, важно для корректной работы анимации.
     */
    setTimeout(() => {
      var _ref$current, _ref$current2;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.classList.add(classToAdd);
      (_ref$current2 = ref.current) === null || _ref$current2 === void 0 || _ref$current2.classList.remove(classToRemove);
    }, 0);
  }, [hidden]);
  const onTransitionEnd = React.useCallback(e => {
    if (hidden) {
      e.currentTarget.style.setProperty('display', 'none');
    }
  }, [hidden]);

  /**
   * При ховере на тост останавливаем его сокрытие, чтобы пользователь мог взаимодействовать
   * с его контентом столько, сколько требуется. Например, выделить и скопировать текст тоста.
   */
  const onMouseEnter = React.useCallback(() => {
    clearTimeout(currentHideTimerId);
  }, [currentHideTimerId]);

  /**
   * Убираем курсор с тоста - снова инициируем сокрытие по таймауту.
   */
  const onMouseLeave = React.useCallback(() => {
    if (isHidable) {
      const timerId = toastApi === null || toastApi === void 0 ? void 0 : toastApi.hideWithDelay(id, hideDelay);
      setCurrentHideTimerId(timerId);
    }
  }, [toastApi, hideDelay, id, isHidable]);
  const internalLeftAdornment = (() => {
    /**
     * Если хочется кастомизации левой части тоста, то передаётся пропса leftAdornment, работает это ветвление.
     * Не запрещаем передавать null.
     * В таком случае LeftAdornment будет display: none; (см. CSS компонента LeftAdornment)
     */
    if (leftAdornmentRenderFunctionOrJSX !== undefined) {
      return /*#__PURE__*/_jsx(LeftAdornment, {
        children: typeof leftAdornmentRenderFunctionOrJSX === 'function' ? leftAdornmentRenderFunctionOrJSX({
          type,
          id,
          toastApi
        }) : leftAdornmentRenderFunctionOrJSX
      });
    }
    const content = (() => {
      switch (type) {
        case 'error':
          {
            return /*#__PURE__*/_jsx(LeftAdornment, {
              children: /*#__PURE__*/_jsx("span", {
                className: clsx(styles['icon-wrapper'], isMobileDevice && styles['mobile']),
                children: /*#__PURE__*/_jsx(IconFunctionalWarningCircle24, {
                  color: "icon-negative-default"
                })
              })
            });
          }
        case 'success':
          {
            return /*#__PURE__*/_jsx(LeftAdornment, {
              children: /*#__PURE__*/_jsx("span", {
                className: clsx(styles['icon-wrapper'], isMobileDevice && styles['mobile']),
                children: /*#__PURE__*/_jsx(IconFunctionalSuccessCircle24, {
                  color: "icon-positive-default"
                })
              })
            });
          }
        case 'info':
          return null;
        case 'spinner':
          {
            const spinnerSize = isMobileDevice ? 16 : 24;
            return /*#__PURE__*/_jsx(LeftAdornment, {
              children: /*#__PURE__*/_jsx(Spinner, {
                size: spinnerSize
              })
            });
          }
        case 'progress':
          return (progressConfig === null || progressConfig === void 0 ? void 0 : progressConfig.progressPercent) !== undefined && /*#__PURE__*/_jsx(LeftAdornment, {
            children: /*#__PURE__*/_jsx(ProgressBar, {
              progress: progressConfig.progressPercent
            })
          });
        default:
          return null;
      }
    })();
    return content;
  })();
  const internalRightAdornment = (() => {
    /**
     * Если хочется кастомизации правой части тоста, то передаётся пропса rightAdornment, работает это ветвление.
     * Не запрещаем передавать null.
     * В таком случае RightAdornment будет display: none; (см. CSS компонента RightAdornment)
     */
    if (rightAdornmentRenderFunctionOrJSX !== undefined) {
      return /*#__PURE__*/_jsx(RightAdornment, {
        children: typeof rightAdornmentRenderFunctionOrJSX === 'function' ? rightAdornmentRenderFunctionOrJSX({
          type,
          id,
          toastApi
        }) : rightAdornmentRenderFunctionOrJSX
      });
    }
    const isActionButtonsBlockVisible = confirmAction || dismissAction;
    if (isActionButtonsBlockVisible) {
      const hasBothControls = !!(confirmAction && dismissAction);
      const TypographyComponent = isMobileDevice ? UIText2 : ArticleParagraph1;
      return /*#__PURE__*/_jsxs(RightAdornment, {
        hasButtons: true,
        hasBothControls: hasBothControls,
        children: [(progressConfig === null || progressConfig === void 0 ? void 0 : progressConfig.progressText) && /*#__PURE__*/_jsx(ProgressAdornment, {
          progress: progressConfig.progressText
        }), confirmAction && /*#__PURE__*/_jsx("div", {
          className: clsx(styles['action-button'], styles['confirm'], !hasBothControls && styles['without-borders']),
          onClick: confirmAction,
          children: /*#__PURE__*/_jsx("div", {
            className: styles['action-text-wrapper'],
            children: /*#__PURE__*/_jsx(TypographyComponent, {
              color: "current_color",
              children: confirmText
            })
          })
        }), dismissAction && /*#__PURE__*/_jsx("div", {
          className: clsx(styles['action-button'], styles['dismiss'], !hasBothControls && styles['without-borders']),
          onClick: dismissAction,
          children: /*#__PURE__*/_jsx("div", {
            className: styles['action-text-wrapper'],
            children: /*#__PURE__*/_jsx(TypographyComponent, {
              color: "current_color",
              children: dismissText
            })
          })
        })]
      });
    }
    const hide = () => {
      onClose === null || onClose === void 0 || onClose();
      toastApi === null || toastApi === void 0 || toastApi.hide(id);
    };
    const content = (() => {
      switch (type) {
        /* Для спиннера по умолчанию нет крестика закрытия */
        case 'spinner':
          return null;
        case 'error':
        case 'success':
        case 'info':
          {
            if (!hasCloseIcon) {
              return null;
            }
            const IconComponent = isMobileDevice ? IconActionClose16 : IconActionClose24;
            return /*#__PURE__*/_jsx(RightAdornment, {
              children: /*#__PURE__*/_jsx(IconComponent, {
                onClick: hide,
                cursor: "pointer"
              })
            });
          }
        case 'progress':
          {
            const IconComponent = isMobileDevice ? IconActionClose16 : IconActionClose24;
            return /*#__PURE__*/_jsxs(RightAdornment, {
              children: [(progressConfig === null || progressConfig === void 0 ? void 0 : progressConfig.progressText) && /*#__PURE__*/_jsx(ProgressAdornment, {
                progress: progressConfig.progressText
              }), /*#__PURE__*/_jsx("div", {
                className: clsx(styles['progress-close'], isMobileDevice && styles['mobile']),
                children: /*#__PURE__*/_jsx(IconComponent, {
                  onClick: hide,
                  cursor: "pointer"
                })
              })]
            });
          }
        default:
          return null;
      }
    })();
    return content;
  })();
  const content = (() => {
    /**
     * Если хочется кастомизации основного контент-блока, то передаётся пропса content, работает это ветвление.
     * Не запрещаем передавать null. В таком случае селектор content будет display: none;
     */
    if (contentRenderFunctionOrJSX !== undefined) {
      return /*#__PURE__*/_jsx("section", {
        className: clsx(styles['content'], isMobileDevice && styles['mobile'], !internalLeftAdornment && styles['extended-left-padding']),
        children: typeof contentRenderFunctionOrJSX === 'function' ? contentRenderFunctionOrJSX({
          type,
          id,
          toastApi
        }) : contentRenderFunctionOrJSX
      });
    }
    if (text) {
      const TypographyComponent = isMobileDevice ? UIText2 : ArticleParagraph1;
      return /*#__PURE__*/_jsx("section", {
        className: clsx(styles['text-content'], isMobileDevice && styles['mobile'], !internalLeftAdornment && styles['extended-left-padding']),
        children: /*#__PURE__*/_jsx(TypographyComponent, {
          color: "text-inverted-default",
          children: text
        })
      });
    }
    return null;
  })();
  return /*#__PURE__*/_jsxs("div", {
    className: clsx(styles['toast'], styles[placement], styles[type], styles[size], isMobileDevice && styles['mobile']),
    ref: ref,
    "data-type": type,
    "data-id": id,
    "data-testid": dataTestId,
    onTransitionEnd: onTransitionEnd,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    children: [internalLeftAdornment, content, internalRightAdornment]
  });
};

/**
 * [Ссылка на макет](https://www.figma.com/design/4UM8zoowH9YGMM7fSK0rhD/%F0%9F%8C%90-Web-Components?node-id=45397-4716)
 */
export const Toast = /*#__PURE__*/React.memo(ToastComponent);